/**
 * Consulta e gravações do banco de horas pelo TanStack Query.
 *
 * Papel: aqui ficam `useBancoDeHoras`, que mantém em cache o banco de horas
 * inteiro, e as cinco mutações — colaborador, lançamento mensal e importação —,
 * que gravam e mandam ler tudo de novo.
 *
 * Como usar: toda mutação invalida `CHAVE_BANCO_DE_HORAS` e só resolve depois
 * da releitura, porque o `onSuccess` devolve a promessa do `invalidateQueries`:
 * quem fecha uma caixa depois do `mutateAsync` já encontra a tela com o dado
 * novo.
 */
import {
  type ImportacaoBancoHoras,
  type NovoColaboradorBancoHoras,
  type NovoLancamentoMensal,
} from '@concierge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  atualizarColaboradorBancoHoras,
  atualizarLancamentoMensal,
  buscarBancoDeHoras,
  criarColaboradorBancoHoras,
  criarLancamentoMensal,
  excluirColaboradorBancoHoras,
  excluirLancamentoMensal,
  importarBancoHoras,
} from '../api/banco-horas';
import { CHAVE_RH } from './usar-rh';

/** Chave do banco de horas inteiro. */
export const CHAVE_BANCO_DE_HORAS = [...CHAVE_RH, 'banco-horas'] as const;

/**
 * Lê o banco de horas inteiro.
 *
 * @returns Resultado da consulta, com a data de atualização e os colaboradores com os meses.
 */
export function useBancoDeHoras() {
  return useQuery({
    queryKey: CHAVE_BANCO_DE_HORAS,
    queryFn: ({ signal }) => buscarBancoDeHoras(signal),
  });
}

/**
 * Manda ler o banco de horas de novo depois de uma gravação.
 *
 * @returns A função que o `onSuccess` de cada mutação chama.
 */
function useReler() {
  const clienteQuery = useQueryClient();
  return () => clienteQuery.invalidateQueries({ queryKey: CHAVE_BANCO_DE_HORAS });
}

interface SalvarColaboradorVariaveis {
  /** O colaborador a alterar, ou `null` para cadastrar um novo. */
  id: string | null;
  dados: NovoColaboradorBancoHoras;
}

/**
 * Cadastra ou altera um colaborador do banco de horas.
 *
 * @returns A mutação, com `{ id, dados }`.
 */
export function useSalvarColaboradorBancoHoras() {
  const reler = useReler();
  return useMutation({
    mutationFn: ({ id, dados }: SalvarColaboradorVariaveis) =>
      id === null
        ? criarColaboradorBancoHoras(dados)
        : atualizarColaboradorBancoHoras(id, dados),
    onSuccess: reler,
  });
}

/**
 * Remove um colaborador do banco de horas, com o histórico.
 *
 * @returns A mutação, com o `id`.
 */
export function useExcluirColaboradorBancoHoras() {
  const reler = useReler();
  return useMutation({
    mutationFn: (id: string) => excluirColaboradorBancoHoras(id),
    onSuccess: reler,
  });
}

interface SalvarLancamentoVariaveis {
  /** O lançamento a alterar, ou `null` para lançar um mês novo. */
  id: string | null;
  dados: NovoLancamentoMensal;
}

/**
 * Lança ou altera um mês.
 *
 * @returns A mutação, com `{ id, dados }`.
 */
export function useSalvarLancamentoMensal() {
  const reler = useReler();
  return useMutation({
    mutationFn: ({ id, dados }: SalvarLancamentoVariaveis) =>
      id === null ? criarLancamentoMensal(dados) : atualizarLancamentoMensal(id, dados),
    onSuccess: reler,
  });
}

/**
 * Remove um mês.
 *
 * @returns A mutação, com o `id`.
 */
export function useExcluirLancamentoMensal() {
  const reler = useReler();
  return useMutation({
    mutationFn: (id: string) => excluirLancamentoMensal(id),
    onSuccess: reler,
  });
}

/**
 * Aplica a importação do extrato.
 *
 * @returns A mutação, com as linhas a gravar.
 */
export function useImportarBancoHoras() {
  const reler = useReler();
  return useMutation({
    mutationFn: (dados: ImportacaoBancoHoras) => importarBancoHoras(dados),
    onSuccess: reler,
  });
}
